import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth, canWrite } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldLabel } from "@/components/FieldLabel";
import ComboBox from "@/components/ComboBox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const writable = canWrite(user);
  const [templates, setTemplates] = useState([]);
  const [t, setT] = useState({ name: "Default template", category: "", body_text: "has successfully participated in {training_title} on {training_date}", signatory_1_name: "", signatory_1_designation: "", signatory_2_name: "", signatory_2_designation: "", is_default: false });
  // Two shapes of the same list: the box below edits it as one comma-separated string,
  // while the category picker needs it as items.
  const [categories, setCategories] = useState("");
  const [cats, setCats] = useState([]);
  const [hints, setHints] = useState({ designations: [] });
  // What the company says about itself on every document it prints.
  const [org, setOrg] = useState({ iso_number: "" });
  const [savingOrg, setSavingOrg] = useState(false);

  const load = async () => {
    const [tp, c, s, o] = await Promise.all([
      api.get("/templates"), api.get("/categories"), api.get("/suggestions"),
      api.get("/settings/organization")]);
    setTemplates(tp.data);
    setCategories(c.data.join(", "));
    setCats(c.data);
    setHints(s.data);
    setOrg(o.data);
    // The default, not the oldest. `tp.data[0]` is whatever was created first, and the
    // certificate is issued from whichever is marked default — so with two templates,
    // Settings showed one while certificates printed the other, and nothing on the
    // screen said which was which. Same "two meanings, one surface" shape as
    // _get_default_template, which had it too.
    if (tp.data.length) setT(tp.data.find((x) => x.is_default) || tp.data[0]);
  };
  useEffect(() => { load(); }, []);

  const saveOrg = async () => {
    setSavingOrg(true);
    try {
      const r = await api.put("/settings/organization", org);
      setOrg(r.data);
      toast.success(r.data.iso_number ? "ISO reference saved — it prints on every document from now on" : "ISO reference cleared");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSavingOrg(false);
  };

  const save = async () => {
    try {
      if (t.id) await api.put(`/templates/${t.id}`, t);
      else await api.post("/templates", t);
      toast.success("Template saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveCategories = async () => {
    try {
      await api.put("/categories", { categories: categories.split(",").map((x) => x.trim()).filter(Boolean) });
      toast.success("Categories updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <div className="overline text-primary">Configuration</div>
        <h2 className="font-heading font-bold text-3xl mt-1">Settings</h2>
      </div>

      <Card className="p-6 border border-border" data-testid="org-settings">
        <h3 className="font-heading font-semibold mb-1">Organisation</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Printed in the footer of every register and attendance sheet, and on each certificate —
          so an auditor reading any of them sees which standard the record is kept under.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-end">
          <div>
            <Label htmlFor="org-iso">ISO certification reference</Label>
            <Input id="org-iso" value={org.iso_number} onChange={(e) => setOrg({ ...org, iso_number: e.target.value })}
                   placeholder="e.g. ISO 9001:2015 · Certificate No. IN-12345" maxLength={120}
                   disabled={!writable} data-testid="org-iso" />
            <p className="text-xs text-muted-foreground mt-1">
              Write it exactly as it should print. Leave it blank and nothing is printed.
            </p>
          </div>
          {writable && (
            <div>
              <Button onClick={saveOrg} disabled={savingOrg} data-testid="save-org">
                {savingOrg ? "Saving..." : "Save"}
              </Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6 border border-border">
        <h3 className="font-heading font-semibold mb-4">Certificate template</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <fieldset className="space-y-3 border-0 p-0 m-0" disabled={!writable}>
            {/* With more than one template there was no way to reach the others at all:
                the form loaded one and every other template was uneditable through the
                UI, permanently. */}
            {templates.length > 1 && (
              <div>
                <Label htmlFor="tpl-pick">Editing</Label>
                <Select value={t.id || ""} onValueChange={(v) => setT(templates.find((x) => x.id === v) || t)}>
                  <SelectTrigger id="tpl-pick" data-testid="tpl-pick"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {templates.map((x) => (
                      <SelectItem key={x.id} value={x.id}>
                        {x.name}{x.is_default ? " · default" : ""}{x.category ? ` · ${x.category}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1.5">
                  A certificate is issued from the <b>default</b>, or from the one matching
                  its category. The wording is pinned to each certificate when it is
                  issued, so editing here changes what future ones say — not ones already
                  issued.
                </p>
              </div>
            )}
            <div><FieldLabel htmlFor="tpl-name" required>Template name</FieldLabel><Input id="tpl-name" value={t.name} onChange={(e) => setT({ ...t, name: e.target.value })} data-testid="tpl-name" /></div>
            <div><Label htmlFor="tpl-category">Category (blank = default)</Label><ComboBox id="tpl-category" testid="tpl-category" value={t.category || ""} onChange={(v) => setT({ ...t, category: v })} options={cats} placeholder="Any category" /></div>
            <div><FieldLabel htmlFor="tpl-body" required>Body text (supports placeholders)</FieldLabel><Textarea id="tpl-body" rows={4} value={t.body_text} onChange={(e) => setT({ ...t, body_text: e.target.value })} data-testid="tpl-body" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="tpl-sig1-name">Signatory 1 name</Label><Input id="tpl-sig1-name" value={t.signatory_1_name || ""} onChange={(e) => setT({ ...t, signatory_1_name: e.target.value })} /></div>
              <div><Label htmlFor="tpl-sig1-desig">Signatory 1 designation</Label><ComboBox id="tpl-sig1-desig" testid="tpl-sig1-desig" value={t.signatory_1_designation || ""} onChange={(v) => setT({ ...t, signatory_1_designation: v })} options={hints.designations || []} placeholder="e.g. Head - HR" /></div>
              <div><Label htmlFor="tpl-sig2-name">Signatory 2 name</Label><Input id="tpl-sig2-name" value={t.signatory_2_name || ""} onChange={(e) => setT({ ...t, signatory_2_name: e.target.value })} /></div>
              <div><Label htmlFor="tpl-sig2-desig">Signatory 2 designation</Label><ComboBox id="tpl-sig2-desig" testid="tpl-sig2-desig" value={t.signatory_2_designation || ""} onChange={(v) => setT({ ...t, signatory_2_designation: v })} options={hints.designations || []} placeholder="e.g. Plant Head" /></div>
            </div>
            <div className="flex items-center gap-3"><Switch checked={!!t.is_default} onCheckedChange={(v) => setT({ ...t, is_default: v })} aria-label="Set as default template" /> <span className="text-sm">Set as default template</span></div>
            {writable && <Button onClick={save} data-testid="save-template">Save template</Button>}
          </fieldset>
          <div className="border border-border rounded-md p-6 bg-muted/30">
            <div className="text-center">
              <div className="overline text-primary">Preview</div>
              {/* The generated PDF carries the letterhead, so a preview without it was
                  showing a document that does not exist. The mark stands alone there, and
                  the preview has to agree or it is describing a different certificate. */}
              <img src="/brand/shakun-logo.svg" alt="" aria-hidden="true"
                   className="h-8 w-auto mx-auto mt-3 dark:hidden" width="135" height="32" />
              <img src="/brand/shakun-logo-light.svg" alt="" aria-hidden="true"
                   className="h-8 w-auto mx-auto mt-3 hidden dark:block" width="135" height="32" />
              <div className="font-heading text-xl font-bold mt-3">Certificate of Participation</div>
              <div className="text-sm text-muted-foreground mt-1">This is to certify that</div>
              <div className="font-heading text-2xl font-bold text-primary mt-4">[Employee Name]</div>
              <div className="text-sm text-muted-foreground mt-3 whitespace-pre-line">{t.body_text}</div>
              <div className="grid grid-cols-2 gap-4 mt-8 text-xs">
                <div className="border-t border-border pt-2">{t.signatory_1_name || "—"}<div className="text-muted-foreground">{t.signatory_1_designation}</div></div>
                <div className="border-t border-border pt-2">{t.signatory_2_name || "—"}<div className="text-muted-foreground">{t.signatory_2_designation}</div></div>
              </div>
              {org.iso_number && (
                <div className="text-[11px] text-muted-foreground mt-4" data-testid="preview-iso">{org.iso_number}</div>
              )}
            </div>
          </div>
        </div>
        {/* Grouped, because a flat list hid the one thing worth knowing: there are two
            units, and "{unit}" is the person's. Written as "at {unit}" it read as where
            the training happened and printed the wrong plant on a certificate. */}
        <div className="mt-4 space-y-1.5 text-xs text-muted-foreground">
          <div>
            <span className="font-medium text-foreground">About the person:</span>{" "}
            <span className="font-mono">{"{employee_name} {employee_code} {designation} {department} {employee_unit}"}</span>
          </div>
          <div>
            <span className="font-medium text-foreground">About the training:</span>{" "}
            <span className="font-mono">{"{training_title} {training_category} {trainer_name} {training_date} {duration_hours} {venue} {training_unit}"}</span>
          </div>
          <div>
            <span className="font-medium text-foreground">About the certificate:</span>{" "}
            <span className="font-mono">{"{certificate_number} {issue_date} {iso_number}"}</span>
          </div>
          <div className="pt-1">
            <b>{"{employee_unit}"}</b> is where the person works. <b>{"{training_unit}"}</b> is
            where the training was held — they are not always the same.{" "}
            <span className="font-mono">{"{unit}"}</span> still works and means the person&apos;s unit.
          </div>
        </div>
      </Card>

      <Card className="p-6 border border-border">
        <h3 className="font-heading font-semibold mb-3">Training categories</h3>
        <div className="flex gap-2">
          <Input value={categories} onChange={(e) => setCategories(e.target.value)} placeholder="Safety, Technical, ..." data-testid="cat-input" disabled={!writable} aria-label="Training categories, comma separated" />
          {writable && <Button onClick={saveCategories} data-testid="save-categories">Save</Button>}
        </div>
      </Card>
    </div>
  );
}
